/**
 * Potansiyel müşteriler state (v0.1.90): the GET /leads/prospects read model, read again every
 * 20 seconds while the page is open and the tab is visible (a live page: scans and the gate
 * change it all day), and the detail of each opened company (GET /leads/prospects/{leadId}),
 * kept until the next refresh of the list. Read only.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { ProspectDetail, ProspectsView } from '../api/prospects.js';
import { useStore } from './context.js';

export const PROSPECTS_REFRESH_MS = 20_000;

export interface ProspectsState {
  view: ProspectsView | null;
  error: string | null;
  loading: boolean;
  /** Local time of the last successful read (for the "canlı" line). */
  refreshedAt: number | null;
  details: Readonly<Record<string, ProspectDetail>>;
  detailErrors: Readonly<Record<string, string>>;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export class ProspectsStore {
  private state: ProspectsState = {
    view: null,
    error: null,
    loading: false,
    refreshedAt: null,
    details: {},
    detailErrors: {},
  };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private watchers = 0;

  constructor(
    private readonly api: TwinApi,
    private readonly clock: () => number = () => Date.now(),
  ) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): ProspectsState => this.state;

  private set(patch: Partial<ProspectsState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  reload(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getProspects();
        this.set({ view, error: null, loading: false, refreshedAt: this.clock() });
      } catch (error) {
        this.set({ error: errorText(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** The page calls this on mount; the returned function stops the refresh on unmount. */
  watch(): () => void {
    this.watchers += 1;
    if (this.state.view === null) void this.reload();
    if (this.timer === null && typeof setInterval === 'function') {
      this.timer = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        void this.reload();
      }, PROSPECTS_REFRESH_MS);
    }
    return () => {
      this.watchers = Math.max(0, this.watchers - 1);
      if (this.watchers === 0 && this.timer !== null) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  async loadDetail(leadId: string, force = false): Promise<void> {
    if (!force && this.state.details[leadId] !== undefined) return;
    try {
      const detail = await this.api.getProspect(leadId);
      this.set({ details: { ...this.state.details, [leadId]: detail } });
    } catch (error) {
      this.set({ detailErrors: { ...this.state.detailErrors, [leadId]: errorText(error) } });
    }
  }
}

export function useProspects(): { store: ProspectsStore; state: ProspectsState } {
  const store = useStore().prospects;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
